// 🧪 Subscription Test Panel
// UI component for easily testing subscription blocking logic

import React, { useState, useEffect } from 'react';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import { 
  createDemoCompany, 
  expireSubscription, 
  extendSubscription, 
  setSubscriptionExpiresIn,
  getSubscriptionStatus 
} from '../utils/demoCompanyUtils';
import { useNotification } from '../contexts/NotificationContext';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Plus,
  Zap
} from 'lucide-react';

const SubscriptionTestPanel = () => {
  const { currentCompany } = useEnhancedCompany();
  const { success, error, warning } = useNotification();
  const [companyId, setCompanyId] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [daysToExpire, setDaysToExpire] = useState(0);
  const [daysAgo, setDaysAgo] = useState(1);
  const [extendDays, setExtendDays] = useState(30);

  useEffect(() => {
    if (currentCompany?.id) {
      setCompanyId(currentCompany.id);
      loadSubscriptionStatus();
    }
  }, [currentCompany]);

  const loadSubscriptionStatus = async () => {
    if (!companyId) return;
    
    try {
      setLoading(true);
      const status = await getSubscriptionStatus(companyId);
      setSubscriptionStatus(status);
    } catch (err) {
      console.error('Error loading subscription status:', err);
      error('Failed to load subscription status');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDemoCompany = async () => {
    try {
      setLoading(true);
      const userId = currentCompany?.ownerId || 'demo_user';
      const result = await createDemoCompany(userId, {
        companyName: `Demo Test ${Date.now()}`,
        planId: 'pro',
        durationDays: 30,
        status: 'active'
      });
      
      success(`Demo company created: ${result.companyId}`, {
        title: 'Demo Company Created',
        details: `Plan: ${result.subscription.basePlan.planName}, Expires: ${new Date(result.endDate).toLocaleDateString()}`
      });
      
      setCompanyId(result.companyId);
      await loadSubscriptionStatus();
    } catch (err) {
      console.error('Error creating demo company:', err);
      error('Failed to create demo company: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExpireNow = async () => {
    if (!companyId) {
      warning('Please enter a company ID');
      return;
    }

    try {
      setLoading(true);
      await expireSubscription(companyId, daysAgo);
      success(`Subscription expired (${daysAgo} day(s) ago)`, {
        title: 'Subscription Expired',
        details: 'Try to create/update an invoice to test blocking'
      });
      await loadSubscriptionStatus();
    } catch (err) {
      console.error('Error expiring subscription:', err);
      error('Failed to expire subscription: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExtend = async () => {
    if (!companyId) {
      warning('Please enter a company ID');
      return;
    }

    try {
      setLoading(true);
      const result = await extendSubscription(companyId, extendDays);
      success(`Subscription extended by ${extendDays} days`, {
        title: 'Subscription Extended',
        details: `New expiration: ${new Date(result.newEndDate).toLocaleDateString()}`
      });
      await loadSubscriptionStatus();
    } catch (err) {
      console.error('Error extending subscription:', err);
      error('Failed to extend subscription: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetExpiresIn = async () => {
    if (!companyId) {
      warning('Please enter a company ID');
      return;
    }

    try {
      setLoading(true);
      const result = await setSubscriptionExpiresIn(companyId, daysToExpire);
      success(`Subscription set to expire in ${daysToExpire} days`, {
        title: 'Expiration Date Updated',
        details: `Expires: ${new Date(result.endDate).toLocaleDateString()}`
      });
      await loadSubscriptionStatus();
    } catch (err) {
      console.error('Error setting expiration:', err);
      error('Failed to set expiration: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { label: 'Expire Now (1 day ago)', days: -1, action: () => setDaysAgo(1) && handleExpireNow() },
    { label: 'Expire 3 Days Ago', days: -3, action: () => setDaysAgo(3) && handleExpireNow() },
    { label: 'Expire 7 Days Ago', days: -7, action: () => setDaysAgo(7) && handleExpireNow() },
    { label: 'Expires Today', days: 0, action: () => setDaysToExpire(0) && handleSetExpiresIn() },
    { label: 'Expires in 1 Day', days: 1, action: () => setDaysToExpire(1) && handleSetExpiresIn() },
    { label: 'Expires in 3 Days', days: 3, action: () => setDaysToExpire(3) && handleSetExpiresIn() },
    { label: 'Extend 30 Days', days: 30, action: () => setExtendDays(30) && handleExtend() },
    { label: 'Extend 90 Days', days: 90, action: () => setExtendDays(90) && handleExtend() },
  ];

  return (
    <div className="p-6 bg-white rounded-lg shadow-lg max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-500" />
            Subscription Test Panel
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Quickly test subscription blocking logic without waiting for expiration
          </p>
        </div>
        <button
          onClick={loadSubscriptionStatus}
          disabled={loading}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Current Company Selection */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Company ID
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={companyId}
            onChange={(e) => setCompanyId(e.target.value)}
            placeholder="Enter company ID or use current company"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleCreateDemoCompany}
            disabled={loading}
            className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50 flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Create Demo
          </button>
        </div>
        {currentCompany?.id && (
          <p className="text-xs text-gray-500 mt-1">
            Current company: {currentCompany.id} ({currentCompany.firmName || currentCompany.name})
          </p>
        )}
      </div>

      {/* Subscription Status */}
      {subscriptionStatus && (
        <div className="mb-6 p-4 rounded-lg border-2" style={{
          borderColor: subscriptionStatus.isExpired ? '#ef4444' : subscriptionStatus.daysRemaining <= 7 ? '#f59e0b' : '#10b981',
          backgroundColor: subscriptionStatus.isExpired ? '#fef2f2' : subscriptionStatus.daysRemaining <= 7 ? '#fffbeb' : '#f0fdf4'
        }}>
          <div className="flex items-start gap-3">
            {subscriptionStatus.isExpired ? (
              <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            ) : subscriptionStatus.daysRemaining <= 7 ? (
              <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
            ) : (
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <h3 className="font-bold text-gray-800 mb-2">
                {subscriptionStatus.planName || subscriptionStatus.planId} - {subscriptionStatus.status}
              </h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-600">Status:</span>{' '}
                  <span className={`font-semibold ${
                    subscriptionStatus.isExpired ? 'text-red-600' : 
                    subscriptionStatus.daysRemaining <= 7 ? 'text-yellow-600' : 
                    'text-green-600'
                  }`}>
                    {subscriptionStatus.isExpired ? 'EXPIRED' : 'ACTIVE'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600">Days Remaining:</span>{' '}
                  <span className="font-semibold">
                    {subscriptionStatus.isExpired 
                      ? `${Math.abs(subscriptionStatus.daysRemaining)} days ago`
                      : `${subscriptionStatus.daysRemaining} days`
                    }
                  </span>
                </div>
                {subscriptionStatus.endDate && (
                  <div className="col-span-2">
                    <span className="text-gray-600">Expiration Date:</span>{' '}
                    <span className="font-semibold">
                      {new Date(subscriptionStatus.endDate).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Zap className="w-5 h-5 text-yellow-500" />
          Quick Actions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {quickActions.map((action, index) => (
            <button
              key={index}
              onClick={action.action}
              disabled={loading || !companyId}
              className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>

      {/* Manual Controls */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 mb-3">Manual Controls</h3>

        {/* Expire Subscription */}
        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
          <h4 className="font-semibold text-red-800 mb-3 flex items-center gap-2">
            <XCircle className="w-5 h-5" />
            Expire Subscription
          </h4>
          <div className="flex gap-2">
            <input
              type="number"
              value={daysAgo}
              onChange={(e) => setDaysAgo(parseInt(e.target.value) || 1)}
              min="1"
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
            />
            <span className="self-center text-sm text-gray-600">days ago</span>
            <button
              onClick={handleExpireNow}
              disabled={loading || !companyId}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50"
            >
              Expire Now
            </button>
          </div>
        </div>

        {/* Set Expiration Date */}
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <h4 className="font-semibold text-yellow-800 mb-3 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Set Expiration Date
          </h4>
          <div className="flex gap-2">
            <input
              type="number"
              value={daysToExpire}
              onChange={(e) => setDaysToExpire(parseInt(e.target.value) || 0)}
              min="0"
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
            />
            <span className="self-center text-sm text-gray-600">days from now</span>
            <button
              onClick={handleSetExpiresIn}
              disabled={loading || !companyId}
              className="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 disabled:opacity-50"
            >
              Set Expiration
            </button>
          </div>
        </div>

        {/* Extend Subscription */}
        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
          <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Extend Subscription
          </h4>
          <div className="flex gap-2">
            <input
              type="number"
              value={extendDays}
              onChange={(e) => setExtendDays(parseInt(e.target.value) || 30)}
              min="1"
              className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <span className="self-center text-sm text-gray-600">days</span>
            <button
              onClick={handleExtend}
              disabled={loading || !companyId}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-50"
            >
              Extend
            </button>
          </div>
        </div>
      </div>

      {/* Testing Instructions */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="font-semibold text-blue-800 mb-2">Testing Instructions</h4>
        <ol className="list-decimal list-inside space-y-1 text-sm text-blue-700">
          <li>Enter or create a demo company ID</li>
          <li>Use "Expire Now" to immediately expire the subscription</li>
          <li>Try to create or update an invoice - it should be blocked</li>
          <li>Use "Extend" to reactivate the subscription</li>
          <li>Try operations again - they should work</li>
        </ol>
      </div>
    </div>
  );
};

export default SubscriptionTestPanel;













